import React, { useEffect, useState, useRef } from "react";
import api from "../api.js";
import {
  Card, CardContent, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, Tabs, Tab, Box, IconButton, Snackbar,
  Typography, CircularProgress, Tooltip, useMediaQuery, Stack, CardActions
} from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Legend, ReferenceDot
} from "recharts";

// ================= FULL domainsData ==================
const domainsData = [
  {
    domain: "Intellectual Acuity & Cognitive Style",
    description: "This domain shows how you think and solve problems. It covers whether you prefer digging into the details or seeing the big picture, and how easily you switch between ideas. Understanding your style can reveal unique strengths and blind spots in your approach to learning and communication.",
    axes: [
      {
        axis: "Analytical Depth",
        high: "You tend to break things down into small, clear pieces and enjoy understanding every detail.",
        low: "You quickly see the overall picture and focus on the main themes rather than the small parts."
      },
      {
        axis: "Cognitive Flexibility & Abstraction",
        high: "You are comfortable changing your viewpoint and mixing ideas to come up with creative solutions.",
        low: "You prefer a straightforward, step-by-step way of thinking, sticking to familiar methods."
      }
    ],
    quadrants: {
      Q1: {
        id: "x-high,y-high",
        description: "People in the far corner of this box tend to combine intense detail focus with creative flexibility. In a healthy expression, they integrate deep analysis with imaginative leaps to uncover breakthrough insights; when unbalanced, they may get lost in details or overcomplicate simple ideas."
      },
      Q2: {
        id: "x-low,y-high",
        description: "People in the far corner of this box tend to see the big picture and generate innovative ideas. In a healthy expression, they inspire others with broad, visionary thinking; when unbalanced, they might overlook key details that could strengthen their ideas."
      },
      Q3: {
        id: "x-low,y-low",
        description: "People in the far corner of this box tend to rely on intuitive, big-picture thinking paired with a conventional approach. In a healthy expression, they capture the essence of problems efficiently; when unbalanced, they may miss opportunities for deeper analysis and creative innovation."
      },
      Q4: {
        id: "x-high,y-low",
        description: "People in the far corner of this box tend to focus on meticulous, systematic analysis and a step-by-step method. In a healthy expression, this yields reliable, precise solutions; when unbalanced, it may constrain their ability to embrace unconventional, creative ideas."
      }
    }
  },
  {
    domain: "Emotional Regulation & Resilience",
    description: "This domain is about how you experience and manage your emotions and stress. It looks at whether you maintain a steady calm or feel emotions with vivid immediacy, and how you bounce back when challenges arise. Recognizing your tendencies here can highlight both your inner strength and potential areas for growth.",
    axes: [
      {
        axis: "Emotional Regulation",
        high: "You keep your emotions steady and respond in a calm, measured way.",
        low: "You feel your emotions strongly and react quickly, showing genuine, immediate responses."
      },
      {
        axis: "Stress Resilience & Outlook",
        high: "You stay calm under pressure and look forward with a flexible mindset.",
        low: "You focus on the here-and-now, dealing with stress by addressing current challenges as they come."
      }
    ],
    quadrants: {
      Q1: {
        id: "x-high,y-high",
        description: "People in the far corner of this box tend to maintain a calm, steady emotional state while keeping a long-term perspective. In a healthy expression, they balance measured responses with visionary planning; when unbalanced, they may inadvertently suppress important emotional cues."
      },
      Q2: {
        id: "x-low,y-high",
        description: "People in the far corner of this box tend to experience emotions vividly while still maintaining a future-oriented outlook. In a healthy expression, their emotional intensity fuels transformative growth; when unbalanced, it can lead to bursts of energy that are difficult to control."
      },
      Q3: {
        id: "x-low,y-low",
        description: "People in the far corner of this box tend to feel emotions strongly and focus on immediate challenges. In a healthy expression, this immediacy offers authenticity and prompt responsiveness; when unbalanced, it may result in reactive patterns that hinder long-term planning."
      },
      Q4: {
        id: "x-high,y-low",
        description: "People in the far corner of this box tend to manage their emotions well while concentrating on current issues. In a healthy expression, they provide stability in the moment; when unbalanced, they might miss opportunities to plan for the future."
      }
    }
  },
  {
    domain: "Interpersonal Engagement & Social Influence",
    description: "This domain explores how you connect with others and express your ideas. It examines whether you thrive in energetic group settings or prefer intimate one-on-one exchanges, and whether you communicate in a direct or a gentle, thoughtful way. These insights can reveal how your social style impacts both your relationships and your influence.",
    axes: [
      {
        axis: "Social Engagement",
        high: "You enjoy meeting lots of people and feel energized in group settings.",
        low: "You prefer meaningful, one-on-one conversations and quieter interactions."
      },
      {
        axis: "Persuasive & Assertive Communication",
        high: "You share your ideas directly and confidently, making it easy for others to understand you.",
        low: "You choose your words carefully, expressing your thoughts in a thoughtful and gentle way."
      }
    ],
    quadrants: {
      Q1: {
        id: "x-high,y-high",
        description: "People in the far corner of this box tend to be highly sociable and direct in their communication. In a healthy expression, their energy and clarity inspire group momentum; when unbalanced, their assertiveness may overwhelm quieter voices."
      },
      Q2: {
        id: "x-low,y-high",
        description: "People in the far corner of this box tend to prefer deep, personal connections while expressing their ideas clearly. In a healthy expression, they create powerful one-on-one impacts; when unbalanced, their influence might not extend effectively to larger groups."
      },
      Q3: {
        id: "x-low,y-low",
        description: "People in the far corner of this box tend to engage in thoughtful, intimate interactions with a gentle style. In a healthy expression, their careful communication builds deep trust; when unbalanced, they might struggle to have their ideas heard in broader contexts."
      },
      Q4: {
        id: "x-high,y-low",
        description: "People in the far corner of this box tend to enjoy diverse social interactions and communicate with a warm touch. In a healthy expression, they create inclusive, supportive environments; when unbalanced, they may find it challenging to assert decisive viewpoints when needed."
      }
    }
  },
  {
    domain: "Motivational & Value Orientation",
    description: "This domain looks at what drives you and the values you hold dear. It explores whether your motivation comes from within or from external influences, and whether your principles are firmly fixed or adapt fluidly to circumstances. These insights can help you understand the source of your drive and the consistency of your values.",
    axes: [
      {
        axis: "Intrinsic Motivation & Autonomy",
        high: "You are self-motivated and enjoy setting and pursuing your own goals.",
        low: "You draw energy from working with others and value the input you get from your community."
      },
      {
        axis: "Ethical, Cultural & Moral Values",
        high: "You stick to clear principles that guide your choices and actions.",
        low: "You are flexible with your values, adapting them based on different situations and practical needs."
      }
    ],
    quadrants: {
      Q1: {
        id: "x-high,y-high",
        description: "People in the far corner of this box tend to pursue their goals with strong inner drive and fixed principles. In a healthy expression, they demonstrate remarkable clarity and purpose; when unbalanced, their rigidity may limit openness to new insights."
      },
      Q2: {
        id: "x-low,y-high",
        description: "People in the far corner of this box tend to draw motivation from external sources while upholding clear values. In a healthy expression, they provide a reliable moral compass; when unbalanced, they might underutilize their own internal drive."
      },
      Q3: {
        id: "x-high,y-low",
        description: "People in the far corner of this box tend to be flexible in both motivation and values. In a healthy expression, their adaptability fosters innovation and responsiveness; when unbalanced, it can lead to uncertainty about personal direction."
      },
      Q4: {
        id: "x-low,y-low",
        description: "People in the far corner of this box tend to be driven by strong inner ambitions while remaining flexible in their principles. In a healthy expression, this blend sparks bold innovation and self-reliance; when unbalanced, it may challenge them to find consistency in their evolving values."
      }
    }
  },
  {
    domain: "Leadership, Influence & Conflict Management",
    description: "This domain focuses on how you lead and manage disagreements. It examines whether you inspire others with a shared vision or prefer clear, direct guidance, and how you resolve conflicts—either cooperatively or decisively. These insights can illuminate the unique impact of your leadership style.",
    axes: [
      {
        axis: "Leadership Style",
        high: "You like to inspire and bring people together with a shared vision.",
        low: "You focus on clear, direct actions and making decisions in a structured way."
      },
      {
        axis: "Conflict Management",
        high: "You try to solve problems together and seek common ground during disagreements.",
        low: "You tackle conflicts head-on with clear actions and firm boundaries."
      }
    ],
    quadrants: {
      Q1: {
        id: "x-high,y-high",
        description: "People in the far corner of this box tend to lead by uniting others with an inspiring vision while resolving conflicts cooperatively. In a healthy expression, they build deep trust and collective momentum; when unbalanced, they might hesitate to make tough decisions swiftly."
      },
      Q2: {
        id: "x-low,y-high",
        description: "People in the far corner of this box tend to combine clear, straightforward leadership with a cooperative approach to conflict. In a healthy expression, they create unity and clarity; when unbalanced, they might miss opportunities to inspire a broader vision.."
      },
      Q3: {
        id: "x-low,y-low",
        description: "People in the far corner of this box tend to lead with assertive, direct actions and resolve conflicts decisively. In a healthy expression, they offer clear guidance; when unbalanced, their approach may come off as overly rigid, limiting collaboration."
      },
      Q4: {
        id: "x-high,y-low",
        description: "People in the far corner of this box tend to blend an inspiring, big-picture vision with firm, decisive conflict resolution. In a healthy expression, they balance authority with empathy; when unbalanced, they risk stifling creative collaboration with excessive control."
      }
    }
  },
  {
    domain: "Decision-Making & Behavioral Control",
    description: "This domain examines how you make choices and manage impulses. It looks at whether you carefully weigh risks or act quickly, and how you balance long-term planning with immediate needs. Understanding these tendencies can help you fine-tune your decision-making process.",
    axes: [
      {
        axis: "Risk & Impulse Control",
        high: "You carefully consider risks and balance bold actions with thoughtful planning.",
        low: "You tend to act quickly, grabbing opportunities as soon as they appear"
      },
      {
        axis: "Decision Efficiency & Temporal Orientation",
        high: "You make fast decisions that keep long-term goals in mind.",
        low: "You take more time to focus on the present details, ensuring your choices fit current needs."
      }
    ],
    quadrants: {
      Q1: {
        id: "x-high,y-high",
        description: "People in the far corner of this box tend to merge careful risk evaluation with swift, forward-thinking decisions. In a healthy expression, this balance transforms challenges into innovative opportunities; when unbalanced, haste might undermine thorough analysis."
      },
      Q2: {
        id: "x-low,y-high",
        description: "People in the far corner of this box tend to act quickly on impulse while keeping future goals in view. In a healthy expression, their spontaneity drives rapid innovation; when unbalanced, they may overlook crucial details."
      },
      Q3: {
        id: "x-high,y-low",
        description: "People in the far corner of this box tend to rely on immediate, impulsive responses with a focus on current details. In a healthy expression, they produce practical solutions; when unbalanced, their decisions might lack a broader perspective."
      },
      Q4: {
        id: "x-low,y-low",
        description: "People in the far corner of this box tend to carefully evaluate risks and focus on present details before deciding. In a healthy expression, their methodical approach ensures stability; when unbalanced, it can slow responsiveness in fast-changing situations."
      }
    }
  },
  {
    domain: "Self-Organization & Adaptive Disposition",
    description: "This domain shows how you structure your day and adjust to change. It covers whether you thrive with established routines or prefer a flexible, spontaneous approach. Recognizing your style here can boost productivity and unlock new avenues for personal growth.",
    axes: [
      {
        axis: "Self-Organization & Learning Style",
        high: "You work best with clear routines and organized methods for handling tasks and learning.",
        low: "You prefer a spontaneous, flexible approach that adapts easily to change."
      },
      {
        axis: "Adaptive Disposition & Self-Concept",
        high: "You see yourself as open to change and comfortable adjusting to new situations.",
        low: "You maintain a steady sense of who you are, valuing consistency and a clear self-image."
      }
    ],
    quadrants: {
      Q1: {
        id: "x-high,y-high",
        description: "People in the far corner of this box tend to combine strong organization with an openness to new ideas. In a healthy expression, they plan effectively while embracing creativity; when unbalanced, excessive structure might limit spontaneity."
      },
      Q2: {
        id: "x-low,y-high",
        description: "People in the far corner of this box tend to thrive on spontaneity and adapt quickly to change. In a healthy expression, their fluidity leads to breakthrough ideas; when unbalanced, a lack of routine may hinder consistent follow-through."
      },
      Q3: {
        id: "x-low,y-low",
        description: "People in the far corner of this box tend to favor flexibility in approach while maintaining a consistent self-view. In a healthy expression, this stability provides comfort and reliability; when unbalanced, it might prevent them from seizing new opportunities for growth."
      },
      Q4: {
        id: "x-high,y-low",
        description: "People in the far corner of this box tend to rely on well-established routines and a fixed sense of self. In a healthy expression, their methodical approach offers clarity and dependability; when unbalanced, it may restrict the ability to innovate."
      }
    }
  }
];

// Flattened axes for radar/timeline
const ALL_AXES = domainsData.flatMap(d => d.axes.map(a => a.axis));

// ====== API constants and helpers ======
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";
const API = {
  quad:    `${API_BASE_URL}/api/dashboard/quad_chart`,
  radar:   `${API_BASE_URL}/api/dashboard/radar`,
  radarGoal:`${API_BASE_URL}/api/dashboard/radar_goal`,
  timeline:`${API_BASE_URL}/api/dashboard/timeline`,
  aiSummary:`${API_BASE_URL}/api/dashboard/ai_summary`,
  journal: `${API_BASE_URL}/api/dashboard/journal`,
  setGoal: `${API_BASE_URL}/api/dashboard/set_goal`,
  setPerception:`${API_BASE_URL}/api/dashboard/set_perception`,
};

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

async function apiCall(url, method = "GET", data = undefined) {
  try {
    const response = await api({
      url,
      method,
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
      data,
    });
    return response.data;
  } catch (err) {
    const msg = err?.response?.data?.detail || err?.message || "API Error";
    if (err?.response?.status === 404 && msg === "No results for this user.") {
      throw new Error("No test results found. Please take the Overview Dive.");
    }
    throw new Error(msg);
  }
}

function formatDate(date) {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
}
function intToColor(str) {
  let hash = 0; for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return ("00000" + (hash & 0xFFFFFF).toString(16)).slice(-6);
}

// ================== SUB COMPONENTS ==================

const ReflectionCard = ({ entry, onEdit, onDelete }) => (
  <Card style={{ margin: "12px 0", background: "#f8f8fa" }}>
    <CardContent>
      <Typography variant="caption" color="textSecondary">
        {formatDate(entry.created_at)}
      </Typography>
      <Typography style={{ margin: "8px 0" }}>
        {entry.journal_entry}
      </Typography>
    </CardContent>
    <CardActions>
      <IconButton onClick={() => onEdit(entry)} size="small"><EditIcon /></IconButton>
      <IconButton onClick={() => onDelete(entry)} size="small"><DeleteIcon /></IconButton>
    </CardActions>
  </Card>
);

const JournalModal = ({ open, onClose, onSave, initialText }) => {
  const [text, setText] = useState(initialText || "");
  useEffect(() => { setText(initialText || ""); }, [initialText]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{initialText ? "Edit Reflection" : "Add a Reflection"}</DialogTitle>
            <DialogContent>
            <textarea
              rows={6}
              style={{ width: "100%", padding: 8, fontFamily: "inherit", fontSize: 16 }}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What are you reflecting on?"
            />
          </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(text)} disabled={!text.trim()}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

function QuadChartLegend({ isMobile }) {
  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ fontSize: isMobile ? 12 : 14 }}>
      <span><span style={{ color: "#0090ff", fontWeight: 700 }}>●</span> Baseline</span>
      <span><span style={{ color: "#ff4444", fontWeight: 700 }}>●</span> Stressed</span>
      <span>
        <span style={{ borderBottom: "2px dotted #0090ff", color: "#0090ff", marginRight: 3 }}>◌</span>
        1 SD (Baseline)
      </span>
      <span>
        <span style={{ borderBottom: "2px dotted #ff4444", color: "#ff4444", marginRight: 3 }}>◌</span>
        1 SD (Stressed)
      </span>
    </Stack>
  );
}

function QuadChartSVG({
  chartSize,
  baseline, overall, axes, axisDefs,
  marker, onSetMarker, goalMode, disagreeMode,
  varianceBaseline = {}, varianceOverall = {},
}) {
  const dotSize = chartSize > 250 ? 22 : 16;
  const [ghostMarker, setGhostMarker] = useState(null);
  const svgRef = useRef(null);

  const toPx = (val) => ((val + 100) / 200) * chartSize;
  const getSD = (varObj, axis) => {
    const v = varObj?.[axis];
    return (v != null && v >= 0) ? Math.sqrt(v) : 0;
  };
  const sdX_base = getSD(varianceBaseline, axes[0]);
  const sdY_base = getSD(varianceBaseline, axes[1]);
  const sdX_over = getSD(varianceOverall, axes[0]);
  const sdY_over = getSD(varianceOverall, axes[1]);

  const rx_base = (sdX_base / 200) * chartSize;
  const ry_base = (sdY_base / 200) * chartSize;
  const rx_over = (sdX_over / 200) * chartSize;
  const ry_over = (sdY_over / 200) * chartSize;
  const baselinePos = {
    x: toPx(baseline?.[axes[0]] ?? 0),
    y: chartSize - toPx(baseline?.[axes[1]] ?? 0)
  };
  const overallPos = {
    x: toPx(overall?.[axes[0]] ?? 0),
    y: chartSize - toPx(overall?.[axes[1]] ?? 0)
  };
  const markerPos = marker && {
    x: toPx(marker?.[axes[0]] ?? 0),
    y: chartSize - toPx(marker?.[axes[1]] ?? 0)
  };

  // -------- Mouse/Touch Handlers --------
  const handleMove = (e) => {
    if (!(goalMode || disagreeMode)) return;
    const svgRect = svgRef.current.getBoundingClientRect();
    let x, y;
    if (e.type.startsWith("touch")) {
      const touch = e.touches[0] || e.changedTouches[0];
      x = touch.clientX - svgRect.left;
      y = touch.clientY - svgRect.top;
    } else {
      x = e.clientX - svgRect.left;
      y = e.clientY - svgRect.top;
    }
    if (x < 0 || y < 0 || x > chartSize || y > chartSize) {
      setGhostMarker(null);
      return;
    }
    const valX = ((x / chartSize) * 200 - 100);
    const valY = (100 - (y / chartSize) * 200);
    setGhostMarker({ [axes[0]]: Math.round(valX), [axes[1]]: Math.round(valY) });
  };
  const handleLeave = () => setGhostMarker(null);
  const handleTouchEnd = (e) => {
  if (!(goalMode || disagreeMode) || !onSetMarker || !ghostMarker) return;
  onSetMarker(ghostMarker);
  setGhostMarker(null);
};
  const handleClick = (e) => {
    if (!(goalMode || disagreeMode) || !onSetMarker || !ghostMarker) return;
    onSetMarker(ghostMarker);
    setGhostMarker(null);
  };

  // -------- Render SVG --------
  return (
    <Box sx={{ position: "relative" }}>
      <svg
        ref={svgRef}
        width={chartSize}
        height={chartSize}
        style={{ borderRadius: 12, background: "#fafbfc", border: "1.5px solid #eee", cursor: (goalMode || disagreeMode) ? "crosshair" : undefined }}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        onTouchMove={handleMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleClick}
      >
        {/* Grid */}
        <line x1={0} y1={chartSize / 2} x2={chartSize} y2={chartSize / 2} stroke="#bbb" strokeDasharray="4 4" />
        <line x1={chartSize / 2} y1={0} x2={chartSize / 2} y2={chartSize} stroke="#bbb" strokeDasharray="4 4" />
        {/* SD Ellipses */}
        {(rx_base > 2 || ry_base > 2) && (
          <ellipse
            cx={baselinePos.x}
            cy={baselinePos.y}
            rx={rx_base}
            ry={ry_base}
            fill="none"
            stroke="#0090ff"
            strokeDasharray="5 4"
            strokeWidth={2}
          />
        )}
        {(rx_over > 2 || ry_over > 2) && (
          <ellipse
            cx={overallPos.x}
            cy={overallPos.y}
            rx={rx_over}
            ry={ry_over}
            fill="none"
            stroke="#ff4444"
            strokeDasharray="5 4"
            strokeWidth={2}
          />
        )}
        {/* Points */}
        <circle cx={baselinePos.x} cy={baselinePos.y} r={dotSize / 2} fill="#0090ff" stroke="#fff" strokeWidth={2} />
        <circle cx={overallPos.x} cy={overallPos.y} r={dotSize / 2} fill="#ff4444" stroke="#fff" strokeWidth={2} />
        {/* Ghost Marker */}
        {ghostMarker && (
          <circle
            cx={toPx(ghostMarker[axes[0]])}
            cy={chartSize - toPx(ghostMarker[axes[1]])}
            r={dotSize / 2}
            fill={goalMode ? "#44e05577" : "#33333377"}
            stroke={goalMode ? "#33b040" : "#111"}
            strokeDasharray="2 2"
            strokeWidth={3}
            opacity={0.7}
            pointerEvents="none"
          />
        )}
        {/* Marker (if set) */}
        {marker && (
          <circle
            cx={markerPos.x} cy={markerPos.y}
            r={dotSize / 2}
            fill={goalMode ? "#44e055" : "#222"}
            stroke={goalMode ? "#33b040" : "#111"}
            strokeWidth={2}
            opacity={0.85}
          />
        )}
      </svg>
    </Box>
  );
}

function QuadrantInfoIcons({ chartSize, quadrants }) {
  const corners = [
    { key: 'tl', left: 8, top: 8, id: "Q2" },
    { key: 'tr', left: chartSize - 32, top: 8, id: "Q1" },
    { key: 'bl', left: 8, top: chartSize - 32, id: "Q3" },
    { key: 'br', left: chartSize - 32, top: chartSize - 32, id: "Q4" },
  ];
  return corners.map(({ key, left, top, id }) => (
    <Tooltip
      key={key}
      title={<span style={{ whiteSpace: "pre-line", maxWidth: 220 }}>{quadrants[id].description}</span>}
      placement="top"
      arrow
    >
      <IconButton
        size="small"
        sx={{
          position: "absolute", left, top, zIndex: 4,
          background: "#fff", border: "1.5px solid #ddd", boxShadow: 1, p: 0.6,
          '&:hover': { background: "#f0f7ff" }
        }}
      >
        <InfoOutlinedIcon fontSize="small" sx={{ color: "#1976d2" }} />
      </IconButton>
    </Tooltip>
  ));
}

// ================== MAIN DASHBOARD ==================
export default function Dashboard() {
  const isMobile = useMediaQuery('(max-width: 800px)');

  // --- Core State ---
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [aiSummary, setAiSummary] = useState("");
  const [journalEntries, setJournalEntries] = useState([]);
  const [journalModalOpen, setJournalModalOpen] = useState(false);
  const [journalEditEntry, setJournalEditEntry] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "" });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);


  // Dashboard data
  const [domainData, setDomainData] = useState(null);
  const [activeTestTab, setActiveTestTab] = useState(0);
  const [activeDomainIdx, setActiveDomainIdx] = useState(0);
  const [goalMode, setGoalMode] = useState(false);
  const [disagreeMode, setDisagreeMode] = useState(false);
  const [marker, setMarker] = useState(null);
  const [radarData, setRadarData] = useState(null);
  const [radarGoalData, setRadarGoalData] = useState(null);
  const [timelineData, setTimelineData] = useState([]);
  const [highlightedAxis, setHighlightedAxis] = useState(null);

  
  const handleLegendHover = (axis) => setHighlightedAxis(axis);
  const handleLegendLeave = () => setHighlightedAxis(null);

  // --- Data Fetch ---
  useEffect(() => {
    async function fetchAll() {
      setLoading(true);
      try {
        const [
          quad, radar, radarGoal, timeline, ai, journal
        ] = await Promise.all([
          apiCall(API.quad),
          apiCall(API.radar),
          apiCall(API.radarGoal),
          apiCall(API.timeline),
          apiCall(API.aiSummary),
          apiCall(API.journal)
        ]);
        setDomainData(quad);
        setRadarData(radar);
        setRadarGoalData(radarGoal);
        setTimelineData(timeline);
        setAiSummary(ai.summary);
        setJournalEntries(journal);
        setError("");
      } catch (err) {
        setError(err.message || String(err));
      }
      setLoading(false);
    }
    fetchAll();
  }, []);

  // --- Reflection Handlers ---
  const handleAddReflection = () => {
    setJournalEditEntry(null);
    setJournalModalOpen(true);
  };
  const handleSaveReflection = async (text) => {
    setJournalModalOpen(false);
    try {
      if (journalEditEntry) {
        // EDIT ➜ PUT
        await apiCall(API.journal, "PUT", { id: journalEditEntry.id, journal_entry: text });
      } else {
        // ADD ➜ POST
        await apiCall(API.journal, "POST", { journal_entry: text });
      }
      const journal = await apiCall(API.journal);
      setJournalEntries(journal);
      setSnack({ open: true, message: journalEditEntry ? "Reflection updated." : "Reflection saved." });
    } catch (err) {
      setSnack({ open: true, message: journalEditEntry ? "Failed to update reflection." : "Failed to save reflection." });
    } finally {
      setJournalEditEntry(null);
    }
  };
  const handleEditReflection = (entry) => {
    setJournalEditEntry(entry);
    setJournalModalOpen(true);
  };
  // 1) open the confirm dialog
  const handleDeleteReflection = (entry) => {
    setToDelete(entry);
    setConfirmOpen(true);
  };

  // 2) actually perform deletion on confirm
  const confirmDeleteReflection = async () => {
    try {
      await apiCall(`${API.journal}?id=${toDelete.id}`, "DELETE");
      const journal = await apiCall(API.journal);
      setJournalEntries(journal);
      setSnack({ open: true, message: "Reflection deleted." });
    } catch (err) {
      setSnack({ open: true, message: "Failed to delete reflection." });
    } finally {
      setConfirmOpen(false);
      setToDelete(null);
    }
  };

  // --- Marker Handlers ---
  const handleSetMarker = async (point) => {
    setMarker(point);
    try {
      if (goalMode) {
        await apiCall(API.setGoal, "POST", point);
        setSnack({ open: true, message: "Goal saved!" });
      }
      if (disagreeMode) {
        await apiCall(API.setPerception, "POST", point);
        setSnack({ open: true, message: "Self-perception saved!" });
      }
      // Re-fetch BOTH datasets to update radar and quad
      const [quad, radarGoal] = await Promise.all([
        apiCall(API.quad),
        apiCall(API.radarGoal),
      ]);
      setDomainData(quad);
      setRadarGoalData(radarGoal);  // <---- THIS IS THE KEY LINE
    } catch (err) {
      setSnack({ open: true, message: "Failed to save marker." });
    }
    setGoalMode(false);
    setDisagreeMode(false);
  };

  // --- Loading/Error guards ---
  if (loading) return <Box p={6} textAlign="center"><CircularProgress /></Box>;
  if (error) return <Box color="error.main" p={3}>{error}</Box>;
  if (!domainData) return <Box p={3}>No data to display.</Box>;

  // --- Extract Data for Current Domain ---
  const { baseline, overall, variance, goal_axis_percentages, perceived_axis_percentages } = domainData || {};
  const latestDate = timelineData?.slice(-1)[0]?.date || "";
  const domain = domainsData[activeDomainIdx];
  const AXES = radarGoalData?.axes || []; // guaranteed 14 elements in your order
  const axisX = domain.axes[0].axis;
  const axisY = domain.axes[1].axis;
  const baselineScores = {
    [axisX]: baseline?.[domain.domain]?.[axisX] ?? 0,
    [axisY]: baseline?.[domain.domain]?.[axisY] ?? 0,
  };
  const overallScores = {
    [axisX]: overall?.[domain.domain]?.[axisX] ?? 0,
    [axisY]: overall?.[domain.domain]?.[axisY] ?? 0,
  };
  const markerScores = goalMode ? goal_axis_percentages : (disagreeMode ? perceived_axis_percentages : null);
  const varianceBaseline = variance?.baseline?.[domain.domain] || {};
  const varianceOverall = variance?.overall?.[domain.domain] || {};

  const yAxisWidth = isMobile ? 90 : 140;
  const chartSize = isMobile ? 220 : 320;

  const radarPlotData = AXES.map(ax => ({
  axis: ax,
  Baseline: radarGoalData?.user_raw_baseline?.[ax] ?? 0,
  Stressed: radarGoalData?.user_raw_overall?.[ax] ?? 0,
  Goal: radarGoalData?.goal_axis_percentages?.[ax] ?? 0,
  SelfPerception: radarGoalData?.perceived_axis_percentages?.[ax] ?? 0,
}));

  // --- RENDER ---
  return (
    <Box sx={{ maxWidth: 1200, margin: "0 auto", p: isMobile ? 1 : 3, fontFamily: "Inter, Arial, sans-serif" }}>
      {/* AI Summary at top */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Your Personal Vybly Profile
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, fontSize: "1.12em" }}>
            {aiSummary || <em>No summary yet.</em>}
          </Typography>
          <Button variant="contained" color="primary" onClick={handleAddReflection}>
            Add a reflection
          </Button>
        </CardContent>
      </Card>

      {/* Test selection card */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2 }}>
        <CardContent>
          <Typography variant="h6">See detailed test results</Typography>
          <Tabs
            value={activeTestTab}
            onChange={(_, v) => setActiveTestTab(v)}
            indicatorColor="primary"
            textColor="primary"
            variant={isMobile ? "scrollable" : "standard"}
            sx={{ mt: 2 }}
          >
            <Tab label="Overview" />
            <Tab label="Stressors (coming soon)" />
            {/* Add other test types if needed */}
          </Tabs>
        </CardContent>
      </Card>

      {/* QUAD CHART SECTION */}
      {activeTestTab === 0 && (
        <Box>
          <Card sx={{ mb: 4, borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: isMobile ? 2 : 3 }}>
                Your latest results ({latestDate ? formatDate(latestDate) : "N/A"})
              </Typography>
              {/* DOMAIN TABS */}
              <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 2 }}>
                {domainsData.map((d, i) =>
                  <Button
                    key={d.domain}
                    variant={i === activeDomainIdx ? "contained" : "outlined"}
                    color={i === activeDomainIdx ? "primary" : "inherit"}
                    onClick={() => setActiveDomainIdx(i)}
                    sx={{
                      textTransform: "none",
                      fontSize: isMobile ? 13 : 15,
                      mb: 1,
                      borderRadius: 2,
                      minWidth: 80
                    }}
                  >
                    {d.domain}
                  </Button>
                )}
              </Box>
              <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "flex-start",
                gap: isMobile ? 3 : 6,
                width: "100%",
                justifyContent: "center"
              }}>
                {/* LEFT COLUMN: Quad chart and axis desc */}
                <Box sx={{
                  flex: "0 0 auto",
                  minWidth: isMobile ? "100%" : 370,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  position: "relative",
                }}>
                  {/* Legend at top */}
                  <Box sx={{ mb: 2 }}>
                    <QuadChartLegend isMobile={isMobile} />
                  </Box>
                  {/* Chart + axis desc */}
                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                    {/* Y-axis descriptions */}
                    <Box sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      height: chartSize,
                      minWidth: isMobile ? 110 : yAxisWidth,
                      maxWidth: isMobile ? 160 : yAxisWidth,   // Clamp the width so it never gets extreme
                      pr: isMobile ? 1 : 2,
                      py: 1
                    }}>
                      <Typography
                        variant="body2"
                        sx={{
                          textAlign: "right",
                          color: "#333",
                          mb: 2,
                          fontSize: isMobile ? 13 : 15,
                          wordBreak: "break-word",
                          whiteSpace: "normal"
                        }}>
                        {domain.axes[1].high}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          textAlign: "right",
                          color: "#333",
                          mt: 2,
                          fontSize: isMobile ? 13 : 15,
                          wordBreak: "break-word",
                          whiteSpace: "normal"
                        }}>
                        {domain.axes[1].low}
                      </Typography>
                    </Box>
                    {/* CHART */}
                    <Box sx={{ position: "relative", width: chartSize, height: chartSize }}>
                      <QuadChartSVG
                        chartSize={chartSize}
                        baseline={baselineScores}
                        overall={overallScores}
                        axes={[axisX, axisY]}
                        axisDefs={domain.axes}
                        marker={markerScores}
                        onSetMarker={handleSetMarker}
                        goalMode={goalMode}
                        disagreeMode={disagreeMode}
                        varianceBaseline={varianceBaseline}
                        varianceOverall={varianceOverall}
                      />
                      <QuadrantInfoIcons chartSize={chartSize} quadrants={domain.quadrants} />
                    </Box>
                  </Box>
                  {/* X-axis labels, perfectly aligned */}
                  <Box sx={{
                    display: "flex",
                    width: chartSize,
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 1,
                    ml: `${yAxisWidth}px`
                  }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#333",
                        fontSize: isMobile ? 13 : 15,
                        maxWidth: "40%",
                        whiteSpace: "normal",
                        wordBreak: "break-word"
                      }}>
                      {domain.axes[0].low}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#333",
                        fontSize: isMobile ? 13 : 15,
                        maxWidth: "40%",
                        textAlign: "right",
                        whiteSpace: "normal",
                        wordBreak: "break-word"
                      }}>
                      {domain.axes[0].high}
                    </Typography>
                  </Box>
                </Box>
                {/* RIGHT COLUMN: Title/description/buttons */}
                <Box sx={{
                  flex: 1,
                  minWidth: isMobile ? "100%" : 370,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: isMobile ? "center" : "flex-start",
                  mt: isMobile ? 3 : 0,
                }}>
                  <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
                    {domain.domain}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      mb: 3,
                      color: "#444",
                      maxWidth: 420,
                      fontSize: isMobile ? 15 : 17
                    }}>
                    {domain.description}
                  </Typography>
                  <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setGoalMode(true);
                        setDisagreeMode(false);
                        setSnack({ open: true, message: "Click on the chart to set your goal marker." });
                      }}
                      sx={{ fontSize: isMobile ? 12 : 15, minWidth: 120 }}
                    >
                      SET A GOAL
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        setDisagreeMode(true);
                        setGoalMode(false);
                        setSnack({ open: true, message: "Click on the chart to set your self-perception marker." });
                      }}
                      sx={{ fontSize: isMobile ? 12 : 15, minWidth: 120 }}
                    >
                      I DISAGREE
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Radar Plots */}
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6">Where you stand compared to other Vybly users</Typography>
              <Box sx={{
                display: "flex",
                gap: isMobile ? 2 : 4,
                flexDirection: isMobile ? "column" : "row",
                mt: 2
              }}>
                <Box sx={{ flex: 1, minWidth: isMobile ? 0 : 320 }}>
                  <Typography fontSize={isMobile ? 11 : 13} sx={{ textAlign: "center", mb: 1 }}>
                    Your Z-scores vs Population
                  </Typography>
                  <ResponsiveContainer width="100%" height={isMobile ? 180 : 320}>
                    <RadarChart
                      cx="50%" cy="50%" outerRadius="80%"
                      data={ALL_AXES.map(ax => ({
                        axis: ax,
                        Baseline: radarData?.user_zscores_baseline?.[ax] ?? 0,
                        Stressed: radarData?.user_zscores_overall?.[ax] ?? 0,
                        Population: radarData?.population_zscores?.[ax] ?? 0,
                      }))}
                    >
                      <PolarGrid />
                      <PolarAngleAxis
                        dataKey="axis"
                        tick={({ payload, x, y, textAnchor }) => (
                          <Tooltip title={payload.value}>
                            <text x={x} y={y} textAnchor={textAnchor} fill="#333" fontSize={isMobile ? 10 : 11}>
                              {payload.value.length > 18 ? payload.value.slice(0, 15) + "…" : payload.value}
                            </text>
                          </Tooltip>
                        )}
                      />
                      <PolarRadiusAxis angle={90} domain={[-3, 3]} />
                      <Radar name="Baseline" dataKey="Baseline" stroke="#3885fc" fill="#3885fc" fillOpacity={0.20} />
                      <Radar name="Stressed" dataKey="Stressed" stroke="#ff4444" fill="#ff4444" fillOpacity={0.20} />
                      <Radar name="Population" dataKey="Population" stroke="#888" fill="#888" fillOpacity={0.10} />
                      <Legend
                        iconType="plainline"
                        wrapperStyle={{
                          marginTop: 14,
                          fontSize: isMobile ? 11 : 13,
                          textAlign: "center",
                        }}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </Box>
                <Box sx={{ flex: 1, minWidth: isMobile ? 0 : 320 }}>
                  <Typography fontSize={isMobile ? 11 : 13} sx={{ textAlign: "center", mb: 1 }}>
                    Your Score vs Goals/Self-Perception
                  </Typography>
                  <ResponsiveContainer width="100%" height={isMobile ? 180 : 320}>
                    <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarPlotData}>
                      <PolarGrid />
                      <PolarAngleAxis
                        dataKey="axis"
                        tick={({ payload, x, y, textAnchor }) => (
                          <Tooltip title={payload.value}>
                            <text x={x} y={y} textAnchor={textAnchor} fill="#333" fontSize={isMobile ? 10 : 11}>
                              {payload.value.length > 18 ? payload.value.slice(0, 15) + "…" : payload.value}
                            </text>
                          </Tooltip>
                        )}
                      />
                      <PolarRadiusAxis angle={90} domain={[-100, 100]} />
                      {/* Baseline (Blue, solid) */}
                      <Radar name="Baseline" dataKey="Baseline" stroke="#3885fc" fill="#3885fc" fillOpacity={0.18} />
                      {/* Stressed (Red, solid) */}
                      <Radar name="Stressed" dataKey="Stressed" stroke="#ff4444" fill="#ff4444" fillOpacity={0.18} />
                      {/* Goal (Green, dashed line, no fill) */}
                      <Radar
                        name="Goal"
                        dataKey="Goal"
                        stroke="#44e055"
                        fillOpacity={0}
                        strokeDasharray="7 4"
                        isAnimationActive={false}
                        dot={false}
                        legendType="plainline"
                      />
                      {/* Self-perception (Gray, dashed line, no fill) */}
                      <Radar
                        name="Self-perception"
                        dataKey="SelfPerception"
                        stroke="#333"
                        fillOpacity={0}
                        strokeDasharray="3 4"
                        isAnimationActive={false}
                        dot={false}
                        legendType="plainline"
                      />
                      <Legend
                        iconType="plainline"
                        wrapperStyle={{
                          marginTop: 14,
                          fontSize: isMobile ? 11 : 13,
                          textAlign: "center",
                        }}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Timeline (Line Chart) */}
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6">Your results over time</Typography>
              <Box sx={{
                mt: 1, mb: 1,
                overflowX: isMobile ? "auto" : "visible",
                display: "flex", flexWrap: "wrap",
                alignItems: "center"
              }}>
                {ALL_AXES.map(ax => (
                  <Box
                    key={ax}
                    onMouseEnter={() => handleLegendHover(ax)}
                    onMouseLeave={handleLegendLeave}
                    onTouchStart={() => handleLegendHover(ax)}
                    onTouchEnd={handleLegendLeave}
                    sx={{
                      display: "flex", alignItems: "center", mr: 2, mb: 1, px: 1,
                      borderRadius: 2,
                      background: highlightedAxis === ax ? "#eee" : "transparent",
                      opacity: highlightedAxis && highlightedAxis !== ax ? 0.5 : 1,
                      cursor: "pointer",
                      fontSize: isMobile ? 10 : 12,
                      whiteSpace: "nowrap"
                    }}
                  >
                    <span style={{
                      display: "inline-block", width: 14, height: 3, marginRight: 6,
                      background: "#" + intToColor(ax), borderRadius: 3
                    }} />
                    <Tooltip title={ax}>
                      <span>
                        {ax.length > 20 ? ax.slice(0, 15) + "…" : ax}
                      </span>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
              <ResponsiveContainer width="100%" height={isMobile ? 180 : 320}>
                <LineChart data={timelineData}>
                  <XAxis dataKey="date" tick={{ fontSize: isMobile ? 9 : 11 }} />
                  <YAxis domain={[-100, 100]} tick={{ fontSize: isMobile ? 9 : 11 }} />
                  {ALL_AXES.map(ax =>
                    <Line
                      key={ax + "_baseline"}
                      dataKey={ax + "_baseline"}
                      stroke={"#" + intToColor(ax)}
                      dot={false}
                      strokeWidth={highlightedAxis === ax ? 3 : 1.6}
                      opacity={highlightedAxis && highlightedAxis !== ax ? 0.13 : 1}
                      isAnimationActive={false}
                      name={ax + " (Baseline)"}
                      strokeDasharray="2 2"
                    />
                  )}
                  {ALL_AXES.map(ax =>
                    <Line
                      key={ax + "_overall"}
                      dataKey={ax + "_overall"}
                      stroke={"#" + intToColor(ax)}
                      dot={false}
                      strokeWidth={highlightedAxis === ax ? 3 : 1.6}
                      opacity={highlightedAxis && highlightedAxis !== ax ? 0.13 : 1}
                      isAnimationActive={false}
                      name={ax + " (Stressed)"}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Reflections */}
      <Card sx={{ mt: 4, borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6">Reflections</Typography>
          {journalEntries.length === 0 && <Typography color="textSecondary" sx={{ mt: 2 }}>No reflections yet.</Typography>}
          {journalEntries.map(entry =>
            <ReflectionCard key={entry.id} entry={entry} onEdit={handleEditReflection} onDelete={handleDeleteReflection} />
          )}
        </CardContent>
      </Card>

            <JournalModal
              open={journalModalOpen}
              onClose={() => { setJournalModalOpen(false); setJournalEditEntry(null); }}
              onSave={handleSaveReflection}
              initialText={journalEditEntry?.journal_entry || ""}
            />

          <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
            <DialogTitle>Delete this reflection?</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mt: 1 }}>
                This will permanently remove the selected reflection.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button color="error" variant="contained" onClick={confirmDeleteReflection}>
                Delete
              </Button>
            </DialogActions>
          </Dialog>

      <Snackbar open={snack.open} message={snack.message} autoHideDuration={3000} onClose={() => setSnack({ open: false, message: "" })} />
    </Box>
  );
}